//Library Imports
import { Select, Tag, Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import store, { RootState } from "store";

//Local Imports
import Speed from "assets/img/icons/Speed.svg";
import { ANALYTICS_FILTERS } from "constants/types";
import {
  getConstantCampaignEmailId,
  resetAllTabsFilter,
  resetContentSearch,
  setSortDirection,
} from "modules/Analytics/utils";
import TableSort from "modules/Common/Components/TableSort";
import { getAnalyticsFilterId, getQueryCampaignId, getQueryEmailId, getQueryEmailIds, getQueryIsAIEmail, getQueryWorkflowId } from "modules/EmailBrowser/utils";
import {
  addCommaToNumber,
  getImageUrl,
  getMonthDates,
  getQuarterDates,
  removeCampaignAndEmailQueryParams,
} from "utils";
import Logo from "../../../assets/img/xiqPlaceholder.jpg";
import ContactListLoader from "../Contacts/ContactListLoader";
import { analyticsDatesFilterChange, selectAsset } from "../Dashboard/actions";
import { getPaginatedContentData } from "./actions";
import ContactEmailDropdown from "./ContactEmailDropdown";

//CSS Imports
import styled from "styled-components";
import { moveSelectedFiltersToApplied } from "../actions";
import { ANALYTICS_DEFAULT_DATES_FILTER } from "../Contacts/utils";
import ContactEmailDropdownSentEmail from "./ContactEmailDropdownContactEmail";
import { DEFAULT_IMAGE_PLACEHOLDER } from "constants/index";
import { Link } from "react-router-dom";
import { resetAnalyticsContent } from "../Contacts/action";
import { getPersonalityLogo } from "constants/utilsStyles";
import { disabledLinks } from "./utils";

interface IContentList {
  searchedText: string;
  setSearchedText: (value: string) => void;
}
const ContentList: React.FC<IContentList> = ({ searchedText, setSearchedText }) => {
  //Constants
  const { Option } = Select;
  const DEFAULT_PAGE_LIMIT = 20;

  //Local States
  const [firstTime, setFirst] = useState(true);
  const [sortBy, setSortBy] = useState("");
  const [sortType, setSortType] = useState({ accessor: "", sortDirection: "none" });
  const [offset, setOffset] = useState(0);
  const [ImgWidth, setImgWidth] = useState<number>(0);
  const [ImgHeight, setImgHeight] = useState<number>(0);
  const ImgDimensionsRef = useRef<any>();

  //Redux States
  const filterReset = useSelector((state: RootState) => state.analyticsContent.filterReset);
  const searchReset = useSelector((state: RootState) => state.analyticsContent.searchReset);
  const articles = useSelector((state: RootState) => state.analyticsContent.data);
  const totalArticlesCount = useSelector((state: RootState) => state.analyticsContent.totalCount);
  const searchedData = useSelector((state: RootState) => state.analyticsContent.searchedData);
  const loading = useSelector((state: RootState) => state.analyticsContent.loading);
  const loaded = useSelector((state: RootState) => state.analyticsContent.loaded);
  const appliedAssetsIds = useSelector((state: RootState) => state.analyticsDashboard.appliedAssetsIds);
  const appliedCampaignIds = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignIds);
  const appliedWorkflowsIds = useSelector((state: RootState) => state.analyticsDashboard.appliedWorkflowsIds);
  const excludeAssetsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeAssetsIds);
  const excludeCampaignsIds = useSelector((state: RootState) => state.analyticsDashboard.excludeCampaignsIds);
  const selectedDatesObject = useSelector((state: RootState) => state.analyticsGraph.selectedDatesObject);
  const start_date = useSelector((state: RootState) => state.analyticsGraph.startDate);
  const end_date = useSelector((state: RootState) => state.analyticsGraph.endDate);
  const isFilterClicked = useSelector((state: RootState) => state.analyticsFilters.filterApplied);

  const EXTERNAL_THUMBNAILS_COUNT = 4;

  const AllCampaignsFilterCheck = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns
  );
  const { isShareProfile, isDailyIQ, isWeeklyAccountInsights, isAllCampaigns } = useSelector(
    (state: RootState) => state.analyticsDashboard.appliedCampaignsFilters
  );

  const columnSortMap: any = {
    email_name: "1",
    total_clicks: "2",
    article_score: "3",
  };

  const redirectContactDetailUrl = (contactId: any) => {
    let queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("contact_id")) {
      queryParams.delete("contact_id");
    }
    queryParams.append("contact_id", contactId);
    queryParams.append("page_source", "analytics-content");
    return `/analytics/contacts/dashboard/?${queryParams.toString()}`;
  };

  const beforeRedirectToContactDetails = () => {
    resetAnalyticsContent();
  };

  const clickedByDropdown = (emails: any, articleId: number, articleIndex: number) => {
    if (isAllCampaigns) return <div>{addCommaToNumber((emails?.totalCount ?? 0).toString() ?? "")}</div>;

    if (emails?.contacts?.length === 0) {
      return <div>N/A</div>;
    }
    const contacts =
      emails.thumbnailContacts.length === 1
        ? Object.values(emails.thumbnailContacts)
        : emails.thumbnailContacts.length > 1 && emails.thumbnailContacts.length <= EXTERNAL_THUMBNAILS_COUNT
          ? Object.values(emails.thumbnailContacts).slice(0, emails.thumbnailContacts.length - 1)
          : emails.thumbnailContacts.length > EXTERNAL_THUMBNAILS_COUNT
            ? Object.values(emails.thumbnailContacts).slice(0, 4)
            : Object.values(emails.thumbnailContacts);
    return (
      <div className="clickby-wrapper">
        {contacts.map((item: any) => (
          <Link
            to={`${redirectContactDetailUrl(item.contactId)}`}
            className="single-user"
            style={{ backgroundColor: `${item.contactColor ?? "#d9d9d9"}` }} // if no contact color, default color #d9d9d9 will be shown
            onClick={beforeRedirectToContactDetails}
          >
            <Tooltip placement="bottom" title={item?.contactName ?? item?.contactEmail}>
              <img
                src={item.contactImage && item.contactImage !== "" ? item.contactImage : DEFAULT_IMAGE_PLACEHOLDER}
                alt="contact"
              />
            </Tooltip>
          </Link>
        ))}
        {/* <div className="user-count">{emails.contacts.length}+</div> */}
        <ContactEmailDropdown
          articleId={articleId}
          emailsCount={
            emails.contacts.length === 0
              ? 0
              : emails.contacts.length <= EXTERNAL_THUMBNAILS_COUNT
                ? 1
                : emails.contacts.length - EXTERNAL_THUMBNAILS_COUNT
          }
          items={emails.contacts}
          showPlus={emails.contacts.length > 1}
          articleIndex={articleIndex}
        />
      </div>
      // <div className={`dropdown-selecter-email active`}>
      //   <div className={`dropdown-options show`}>
      //     <div className="dropdown-options-inner">
      //       <div className={`dropdown-wrapper`}>
      //         <div className={`dropdown-top-filters body-dropdown`}>
      //           <ContactEmailDropdown
      //             articleId={articleId}
      //             emailsCount={emails.contacts.length}
      //             items={emails.contacts}
      //           />
      //         </div>
      //       </div>
      //     </div>
      //   </div>
      // </div>
    );
  };

  const clickedByDropdownSendEmail = (emails_data: any, articleId: number) => {
    return (
      <div className={`dropdown-selecter-email active`}>
        <div className={`dropdown-options show`}>
          <div className="dropdown-options-inner">
            <div className={`dropdown-wrapper`}>
              <div className={`dropdown-top-filters body-dropdown`}>
                <ContactEmailDropdownSentEmail articleId={articleId} emailsData={emails_data} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getPagination = (resetPagination?: boolean, resetSearch?: boolean, sortParams?: any) => {
    const orderDataBy = sortParams?.orderBy ?? sortType.sortDirection;
    const orderBy = orderDataBy === "none" || orderDataBy.length === 0 ? "" : orderDataBy;

    let campaignIds;
    let emailIds;
    let workflowIds;
    let fsId = getAnalyticsFilterId();

    if (filterReset === true || (searchReset === true && firstTime === false)) {
      campaignIds = appliedCampaignIds;
      emailIds = appliedAssetsIds;
      workflowIds = appliedWorkflowsIds;
    } else {
      if (!fsId && firstTime) {
        const queryCampaignId = getQueryCampaignId();
        const isAIEmail = getQueryIsAIEmail();
        if (appliedCampaignIds.length <= 0 && queryCampaignId > 0 && firstTime === true) {
          campaignIds = appliedCampaignIds.length > 0 ? appliedCampaignIds : [queryCampaignId];
          if (isAIEmail) {
            const emailIds = getQueryEmailIds();
            emailIds.forEach((id: number) => {
              selectAsset(id, queryCampaignId)
            })
          }
          else {
            selectAsset(getQueryEmailId(), queryCampaignId);
          }
          moveSelectedFiltersToApplied();
        } else campaignIds = appliedCampaignIds;
        if (appliedAssetsIds.length <= 0 && getQueryEmailId() > 0 && firstTime === true) {
          if (isAIEmail) {
            emailIds = appliedAssetsIds.length > 0 ? appliedAssetsIds : getQueryEmailIds()
          }
          else {
            emailIds = appliedAssetsIds.length > 0 ? appliedAssetsIds : [getQueryEmailId()];
          }
        } else emailIds = appliedAssetsIds;
        if (appliedWorkflowsIds.length <= 0 && getQueryWorkflowId() > 0 && firstTime === true) {
          workflowIds = appliedWorkflowsIds.length > 0 ? appliedWorkflowsIds : [getQueryWorkflowId()];
        } else workflowIds = appliedWorkflowsIds;
      }
      else {
        campaignIds = appliedCampaignIds;
        emailIds = appliedAssetsIds;
        workflowIds = appliedWorkflowsIds;
      }
    }

    // check if is share profile || daily IQ || weekly accounts insights
    if (isShareProfile || isDailyIQ || isWeeklyAccountInsights) {
      let campaign_email_id = getConstantCampaignEmailId({ isShareProfile, isDailyIQ, isWeeklyAccountInsights });
      campaignIds = [campaign_email_id];
      emailIds = [campaign_email_id];
    }

    let startDate = "";
    let endDate = "";
    if (start_date === "" && end_date === "") {
      analyticsDatesFilterChange(ANALYTICS_DEFAULT_DATES_FILTER);
      if (ANALYTICS_DEFAULT_DATES_FILTER.value === "qtr") {
        startDate = getQuarterDates().start_date;
        endDate = getQuarterDates().end_date;
      } else if (ANALYTICS_DEFAULT_DATES_FILTER.value === "month") {
        startDate = getMonthDates().start_date;
        endDate = getMonthDates().end_date;
      }
    } else {
      startDate = start_date;
      endDate = end_date;
    }

    let params: any = {
      allCampaigns: AllCampaignsFilterCheck,
      searchWord: resetSearch === true ? "" : searchedText,
      limit: DEFAULT_PAGE_LIMIT,
      offset: resetPagination === true ? 0 : articles.length,
      sortBy: orderBy === "" ? "" : columnSortMap[sortParams?.sortBy ?? sortBy] ?? "",
      orderBy: orderBy,
      campaignIds: campaignIds,
      emailIds: emailIds,
      workflowIds,
      startDate: startDate,
      endDate: endDate,
    };

    if (AllCampaignsFilterCheck) {
      params["excludeCampaignsIds"] = excludeCampaignsIds;
      params["excludeAssetsIds"] = excludeAssetsIds;
    }

    return params;
  };
  const sortedResults = (column?: any) => {
    let sort_type = "";
    if (column.canSort) {
      setSortBy(column.id);
      sort_type = setSortDirection(column.id, column.sortDirection);
      setSortType({ accessor: column.id, sortDirection: sort_type });
      getPaginatedContentData(getPagination(true, false, { sortBy: column.id, orderBy: sort_type }));
    }
  };

  // This function calculates width and height of the list
  const getImgSize = () => {
    const newWidth = ImgDimensionsRef?.current?.clientWidth;
    setImgWidth(newWidth);

    const newHeight = ImgDimensionsRef?.current?.clientHeight;
    setImgHeight(newHeight);
  };

  // Update 'width' and 'height' when the window resizes
  useEffect(() => {
    getImgSize();
    window.addEventListener("resize", getImgSize);
    return () => window.removeEventListener("resize", getImgSize);
  }, []);

  const disabledLink = (link: string) => {
    if (link) { return disabledLinks.some((item) => link.includes(item)) }
  }

  const columns = React.useMemo(
    () => [
      {
        Header: " ",
        disableSortBy: true,
        columns: [
          {
            Header: "Clicked on",
            disableSortBy: true,
            Cell: (props: any) => {
              const item = props.row.original;
              return (
                <a className={disabledLink(item.articleLink) ? "disabled" : ""} href={item.articleLink} target="_blank" rel="noreferrer">
                  <div className="d-flex flex-row">
                    <div className="preview">
                      <div
                        className="company-overlay"
                        style={{
                          backgroundImage: `url(${item.articleImage
                            ? getImageUrl(
                              item.articleImage,
                              ImgWidth > 0 ? ImgWidth : 50,
                              ImgHeight > 0 ? ImgHeight : 50,
                              item.articleId
                            )
                            : Logo
                            })`,
                        }}
                      ></div>
                      <img
                        ref={ImgDimensionsRef}
                        src={
                          item.articleImage
                            ? getImageUrl(
                              item.articleImage,
                              ImgWidth > 0 ? ImgWidth : 50,
                              ImgHeight > 0 ? ImgHeight : 50,
                              item.articleId
                            )
                            : Logo
                        }
                        className="company-logo"
                        alt=""
                        onError={(e: any) => {
                          e.target.onerror = null;
                        }}
                      />
                    </div>
                    <div className="d-flex flex-column details">
                      <span className="article_title">{item.articleTitle}</span>
                      <span className="article_link">
                        {/* <a href={item.articleLink} target="_blank" rel="noreferrer"> */}
                        {item.articleLink}
                        {/* </a> */}
                      </span>
                    </div>
                  </div>
                </a>
              );
            },
          },
          {
            Header: "Clicked by",
            disableSortBy: true,
            Cell: (props: any) => {
              const item = props.row.original;
              return clickedByDropdown(item.clickedBy, item.articleId, props.row.index);
            },
          },
          {
            Header: "Clicks",
            id: "total_clicks",
            accessor: "totalClicks",
            sortType: "basic",
            sortDirection: sortType.accessor === "total_clicks" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              const item = props.row.original;
              return <div>{addCommaToNumber((item.totalClicks ?? 0).toString() ?? "")}</div>;
            },
          },
          {
            Header: "Email / Sent",
            disableSortBy: true,
            Cell: (props: any) => {
              const item = props.row.original;
              if (item.emailsData.length === 1) {
                const emailName = item.emailsData[0].emailName;
                const emailSentDate = item.emailsData[0].lastSent;
                const emailSubject = item.emailsData[0].emailSubject;
                const personalityName = item.emailsData[0].personalityName;
                const personality = item.emailsData[0].personality;
                return (
                  <div className="d-flex flex-column">
                    <div style={{ fontWeight: "bold" }}>{emailName}</div>
                    {emailSubject ?
                      <Tooltip title={item.emailSubject} overlayInnerStyle={{ width: 'auto', maxWidth: '400px' }} >
                        <div className="text-truncate-one-line">Subject: <span className="bold-sub">{emailSubject}</span></div>
                      </Tooltip>
                      : null
                    }
                    <div>{emailSentDate}</div>

                    {personalityName ?
                      <div className="personality-badge">
                        <Tooltip title={personalityName}>
                          <Tag className={`styling-tag ai-type-tag ${personalityName}`}><img src={getPersonalityLogo(personalityName)} alt="personality" className="logo-img" /></Tag>
                        </Tooltip>
                      </div> : null
                    }

                  </div>
                );
              } else {
                return clickedByDropdownSendEmail(item.emailsData, item.articleId);
              }
            },
          },
          // {
          //   Header: "Email / Sent",
          //   id: "email_name",
          //   accessor: "email_name",
          //   sortType: "basic",
          //   sortDirection: sortType.accessor === "email_name" ? sortType.sortDirection : "none",
          //   canSort: true,
          //   Cell: (props: any) => {
          //     const item = props.row.original;
          //     return (
          //       <div className="d-flex flex-column">
          //         <div>{item.emailName}</div>
          //         <div>{item.emailSentDate}</div>
          //       </div>
          //     );
          //   },
          // },
          {
            Header: "Article Score",
            id: "article_score",
            accessor: "articleScore",
            sortType: "basic",
            sortDirection: sortType.accessor === "article_score" ? sortType.sortDirection : "none",
            canSort: true,
            Cell: (props: any) => {
              const item = props.row.original;
              return (
                <div className="last-item">
                  <div className="engagement">
                    <img src={Speed} alt="" />
                    &nbsp;
                    {addCommaToNumber((item.articleScore ?? 0).toString() ?? "")}
                  </div>
                </div>
              );
            },
          },
        ],
      },
    ],
    [sortType, isAllCampaigns]
  );

  useEffect(() => {
    if (!firstTime) {
      getPaginatedContentData(getPagination(true, false));
      resetContentSearch();
    }
  }, [searchedText]);

  useEffect(() => {
    if (isFilterClicked === true) {
      getPaginatedContentData(getPagination(true, false));
      resetAllTabsFilter(true);
      store.dispatch({ type: ANALYTICS_FILTERS.RESET });
    }
  }, [isFilterClicked]);

  // useEffect(() => {
  //   if (
  //     appliedCampaignIds.length === 0 &&
  //     appliedAssetsIds.length === 0 &&
  //     selectedDatesObject?.value === ANALYTICS_DEFAULT_DATES_FILTER.value &&
  //     firstTime === false &&
  //     !loading
  //   ) {
  //     getPaginatedContentData(getPagination(true, false));
  //     removeCampaignAndEmailQueryParams();
  //   }
  // }, [appliedCampaignIds, appliedAssetsIds, selectedDatesObject]);

  useEffect(() => {
    if (articles.length <= 0 || searchReset === true || filterReset === true)
      getPaginatedContentData(getPagination(true, true), setFirst);
    else setFirst(false);
    setSearchedText("");
  }, []);

  return (
    <Wrapper>
      {/* <InfiniteLoader
        dataLength={articles.length}
        next={() => getPaginatedContentData(getPagination(false, false))}
        hasMore={articles.length < totalArticlesCount}
        loader={<ContactListLoader loading={loading} />}
      > */}
      <>
        <TableSort
          columns={columns}
          data={articles}
          module={"content"}
          _onCellClick={sortedResults}
          next={() => getPaginatedContentData(getPagination(false, false))}
          hasMore={articles.length < totalArticlesCount}
          loader={<ContactListLoader loading={loading} />}
          loading={loading}
        />
        {loaded && articles?.length === 0 ? <div className="no-data">No Data Found!</div> : null}
      </>
      {/* </InfiniteLoader> */}
    </Wrapper>
  );
};

export default ContentList;

const Wrapper = styled.div`
  .bold-sub {
    font-weight: 600;
  }
  .personality-badge {
    .ai-type-tag {
      top: 8px;
      right: 0px;
      .logo-img {
        height: 18px;
        width: 18px;
        min-width: 18px;
      }
    }
  }
  .no-data {
    position: absolute;
    top: 20%;
    width: 100%;
    /* height: 100px; */
    height: 50vh;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "robotomedium";
    color: #67696a;
    font-size: 1rem;
    margin-bottom: 30px;
    /* box-shadow: 6px 30px 73px -10px grey; */
    border-radius: 5px;
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: "robotomedium";
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
    white-space: nowrap;
  }
  .company-logo {
    width: 55px;
    height: 55px;
    border-radius: 9px;
  }
  position: relative;
  .disabled {
    pointer-events: none;
    opacity: 0.6;
  }
  .prefix {
    font-weight: 300;
    font-size: 14px;
    color: #999;
  }
  .clickby-wrapper {
    position: relative;
    display: flex;
    cursor: pointer;
    .single-user {
      border-radius: 50%;
      padding: 2px;
      width: 38px;
      height: 38px;
      position: relative;
      z-index: 4;
      img {
        border-radius: 50%;
        height: 34px;
        width: 34px;
      }
      &:nth-of-type(2) {
        margin-left: -13px;
        position: relative;
        z-index: 3;
      }
      &:nth-of-type(3) {
        margin-left: -13px;
        position: relative;
        z-index: 2;
      }
      &:nth-of-type(4) {
        margin-left: -13px;
        position: relative;
        z-index: 1;
      }
    }
    .user-count {
      width: 28px;
      height: 28px;
      background-color: #ffffff;
      box-shadow: 0 0 17px -2px rgb(0, 0, 0, 0.12);
      color: #5e7079;
      font-weight: 700;
      font-size: 10px;
      font-family: "Roboto", sans-serif;
      text-align: center;
      border-radius: 50%;
      position: relative;
      z-index: 5;
      margin-left: -13px;
      margin-top: 7px;
      cursor: pointer;
      letter-spacing: -0.21px;
      padding: 2px 0 0 0;
    }
  }
  .dropdown-selecter-email {
    background: #fff;
    border-radius: 8px;
    min-height: 36px;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding-left: 10px;
    padding-right: 2px;
    height: 38px;
    width: 170px;
    max-width: 100%;
    position: relative;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      /* min-width: 150px; */
      border-radius: 6px;
      height: 30px;
    }
    &:focus,
    &:active,
    &.active {
      /* box-shadow: 0 0 0 0.1rem rgb(40 167 69 / 50%); */
    }
    .label {
      color: #7e7e7e;
      font-size: 16px;
      font-weight: 500;
      font-family: "Roboto", sans-serif;
      flex-wrap: wrap;
      padding: 2px 8px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
        margin-top: 5px;
      }
      img {
        margin-top: -3px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 15px;
        }
      }
    }
    .seprator {
      -webkit-align-self: stretch;
      -ms-flex-item-align: stretch;
      align-self: stretch;
      background-color: hsl(0, 0%, 80%);
      margin-bottom: 8px;
      margin-top: 8px;
      width: 1px;
      box-sizing: border-box;
      + .p-2 {
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          padding-top: 3px !important;
          padding-bottom: 3px !important;
        }
      }
    }
  }
  .dropdown-options {
    position: absolute;
    top: 0px;
    right: 0px;
    z-index: 1;
    min-width: 100%;
    width: auto;
    border-radius: 8px;
    /* width: 280px; */
    transition-duration: 0.5s;
    overflow: hidden;
    height: 0px;
    opacity: 0;
    &.show {
      height: auto;
      opacity: 1;
    }
    .dropdown-options-inner {
      /* border: 1px solid #def6e6;
      box-shadow: 0 0 40px 0 rgb(0 0 0 / 28%);
      background: #fff; 
      border-radius: 8px;
      float: left;
      width: 100%;*/
      .body-dropdown {
        text-align: center;
      }
      .message-limit {
        float: left;
        width: 100%;
        padding: 15px;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
        font-family: "Roboto", sans-serif;
        color: #ffa112;
        background: #ebf5ee;
      }
      .dropdown-header {
        background: #def6e6;
        color: #27ce0a;
        font-weight: 500;
        font-size: 16px;
        line-height: 40px;
        font-family: "Roboto", sans-serif;
        padding: 10px 16px 0 16px;
        border-radius: 8px 8px 0 0;
        float: left;
        width: 100%;
        svg {
          height: 12px;
          width: 16px;
          margin-right: 12px;
        }
        .sub-header {
          border: 1px solid #dadada;
          padding: 10px;
          background: #f7f7f7;
          float: left;
          margin: 10px -17px 0 -17px;
          width: calc(100% + 34px);
        }
      }
      .dropdown-wrapper {
        float: left;
        width: 100%;
        max-height: 450px;
        overflow: auto;
        &::-webkit-scrollbar {
          display: none;
        }
        &.show-part2 {
          .dropdown-top-filters {
            display: none;
          }
          .dropdown-v2 {
            display: block;
          }
        }
        .dropdown-v2 {
          display: none;
          .header-dropdown {
            background: #f7f7f7;
            border-bottom: 1px solid #dadada;
            padding: 16px 16px 12px 16px;
            float: left;
            width: 100%;
            .back-main {
              cursor: pointer;
              float: left;
              width: 100%;
              img {
                width: 15px;
              }
              .back-label {
                padding-left: 8px;
                color: #424b50;
                font-size: 16px;
                font-weight: 500;
                font-family: "Roboto", sans-serif;
              }
            }
            .label-dropwon-v2 {
              color: #7e7e7e;
              font-size: 12px;
              font-weight: 500;
              font-family: "Roboto", sans-serif;
              padding-left: 24px;
              margin-top: -5px;
              float: left;
              display: block;
            }
          }
          .body-dropdown {
            padding: 15px 8px 12px 15px;
            float: left;
            width: 100%;
            .main-label {
              color: #424b50;
              font-size: 14px;
              font-weight: 500;
              font-family: "Roboto", sans-serif;
              margin-top: 10px;
              float: left;
              width: 100%;
            }
            ul {
              margin: 0;
              padding: 0;
              li {
                padding: 5px 0;
                .ant-radio-wrapper {
                  span {
                    color: #7e7e7e;
                    font-size: 14px;
                    font-weight: 500;
                    font-family: "Roboto", sans-serif;
                    letter-spacing: -0.35px;
                  }
                }
                input[type="text"] {
                  float: right;
                  width: 65px;
                  border-radius: 13px;
                  border: 1px solid #dadada;
                  color: #7e7e7e;
                  font-size: 16px;
                  font-weight: 500;
                  font-family: "Roboto", sans-serif;
                  line-height: 16px;
                  padding: 2px 8px;
                  &:focus,
                  &:active,
                  &:focus-visible {
                    outline: none;
                    border: 1px solid #dadada;
                    outline-offset: 0px;
                    border-color: inherit;
                    -webkit-box-shadow: none;
                    box-shadow: none;
                    background: transparent;
                    box-shadow: 0 0 0 0.1rem rgb(40 167 69 / 50%);
                  }
                }
              }
            }
          }
        }
      }
      .dropdown-top-filters {
        /* padding: 16px 16px 16px 16px; */
        /* float: left;
        width: 100%; */
        h4 {
          color: #424b50;
          font-size: 14px;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          margin: 0;
          line-height: 40px;
          width: 100%;
          float: left;
        }
        ul {
          width: 100%;
          float: left;
          display: block;
          padding: 0 0 0 20px;
          margin: 0;
          li {
            list-style-type: none;
            margin: 5px 0;
            width: 100%;
            display: block;
            float: left;
            label {
              font-family: "Roboto", sans-serif;
              color: #7e7e7e;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 0px 0;
              float: left;
              &:hover {
                color: #27ce0a;
              }
              .ant-checkbox-wrapper {
                display: inline-block;
                float: left;
                &::after {
                  display: none;
                }
              }
              > div {
                /* margin-top: -2px; */
                float: left;
                margin: 8px 12px;
              }
            }
          }
        }
      }
      .dropdown-body {
        padding: 0 16px;
        width: 100%;
        float: left;
        ul {
          width: 100%;
          float: left;
          padding: 20px 20px 20px 0px;
          list-style-type: none;
          li {
            width: 100%;
            float: left;
            padding: 0;
            margin: 0;
            .label {
              font-family: "Roboto", sans-serif;
              color: #7e7e7e;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              display: block;
              padding: 5px 0;
              line-height: 30px;
              white-space: nowrap;
              text-overflow: ellipsis;
              word-break: keep-all;
              overflow: hidden;
              width: 165px;
              min-width: 165px;
              transition-duration: 0.5s;
            }
            .tick {
              opacity: 0;
              transition-duration: 0.5s;
              margin-right: 13px;
              float: left;
              svg {
                height: 12px;
                width: 16px;
                vertical-align: baseline;
              }
            }
            .current {
              color: #27ce0a;
              opacity: 1;
            }
          }
        }
      }
    }
  }
  table {
    width: 100%;
    margin-top: 10px;
    margin-bottom: 20px;
    display: block;
    thead {
      border-bottom: 1px solid #dddddd;
      tr {
        padding: 0 15px;
        th {
          color: #5e7079;
          font-weight: 700;
          font-size: 18px;
          font-family: "Roboto", sans-serif;
        }
      }
    }
    tbody {
      width: 100%;
      display: block;
      box-sizing: border-box;
      overflow-y: auto;
      height: calc(100vh - 340px);
      /* padding-bottom: 90px; */
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: calc(100vh - 300px);
      }
      scrollbar-width: thin;
      scrollbar-color: #001760 #efefef;
      &::-webkit-scrollbar {
        width: 5px;
        height: 5px;
        border-radius: 5px;
      }
      /* Track */
      &::-webkit-scrollbar-track {
        background: #fff;
        margin-top: 0;
        margin-bottom: 10px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0;
        }
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #001760;
      }
      > div {
        overflow: hidden;
      }
      tr {
        background-color: #ffffff;
        box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
        width: 100%;
        border-radius: 8px;
        transition-duration: 0.2s;
        position: relative;
        margin: 4px 0;
        padding: 16px 15px 10px 15px;
        display: flex !important;
        align-content: center;
      }
    }
    thead {
      tr {
        th {
          span {
            width: 15px;
            .sort_asc {
              width: 10px;
              height: 8px;
              margin-top: -15px;
              margin-left: 5px;
              float: left;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                margin-top: -13px;
              }
              img {
                transform: rotate(180deg);
              }
            }
            .sort_desc {
              width: 10px;
              height: 8px;
              float: left;
              margin-left: 5px;
              margin-top: 5px;
              img {
                float: left;
              }
            }
          }
        }
      }
      tr:nth-child(1) {
        justify-content: end;
        display: flex;
        th {
          font-size: 18px;
          font-weight: 700;
          color: #5e7079;
          font-family: "Roboto", sans-serif;
          justify-content: center;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(2) {
            width: 380px;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 240px;
            }
          }
          &:nth-child(3) {
            width: 200px;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 120px;
            }
          }
          &:nth-child(4) {
            width: 240px;
            @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
              width: 200px;
            }
          }
        }
      }
      tr:nth-child(2) {
        th {
          font-size: 16px;
          color: #5e7079;
          font-weight: 500;
          font-family: "Roboto", sans-serif;
          white-space: nowrap;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
          &:nth-child(1) {
            padding-left: 15px;
          }
        }
      }
    }
    thead,
    tbody {
      width: 100%;
      display: block;
      float: left;
      tr {
        width: 100%;
        display: block;
        float: left;
        td {
          min-height: 70px;
          font-family: "Roboto", sans-serif;
          font-weight: 500;
          font-size: 20px;
          color: #424b50;
          @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
          }
        }
        th,
        td {
          display: block;
          float: left;
          &:nth-child(1) {
            width: calc(100% - 1090px);
            text-align: left;
            @media all and (max-width: 1621px) {
              width: calc(100% - 750px);
            }
            @media all and (max-width: 1439px) {
              width: calc(100% - 580px);
            }
            > a > div {
              display: flex;
              width: 100%;
              flex-direction: row;
              align-items: start;
              align-content: space-evenly;
              min-height: 100%;
              max-width: 100%;
              max-width: 100%;
              .preview {
                float: left;
                align-self: center;
                border-radius: 10px;
                overflow: hidden;
                padding: 2px;
                position: relative;
                img {
                  border-radius: 10px;
                  box-shadow: #f0b520 0px 0px 0px 2px;
                  min-width: 51px;
                  min-height: 51px;
                  width: 51px;
                  height: 51px;
                }
                .company-overlay {
                  min-width: 51px;
                  min-height: 51px;
                  border-radius: 10px;
                  position: absolute;
                  left: 2px;
                  top: 2px;
                  background-size: cover;
                  background-color: transparent;
                  background-repeat: no-repeat;
                  background-position: center;
                  z-index: 5;
                }
              }
              .details {
                float: left;
                font-weight: 400;
                color: #000000;
                font-size: 20px;
                font-family: "Roboto Slab", serif;
                align-self: center;
                max-width: calc(100% - 120px);
                padding-left: 15px;
                @media all and (max-width: 1125px) {
                  max-width: calc(100% - 70px);
                }
                .article_title {
                  color: #424b50;
                  font-weight: 500;
                  font-size: 16px;
                  line-height: 18px;
                  font-family: "Roboto", sans-serif;
                  max-width: 100%;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  word-break: break-word;
                }
                .article_link {
                  color: #1ec252;
                  font-weight: 500;
                  font-size: 14px;
                  font-family: "Roboto", sans-serif;
                  max-width: 100%;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  display: block;
                }
              }
            }
          }
          &:nth-child(2) {
            width: 200px;
            text-align: left;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1621px) {
              width: 130px;
            }
          }
          &:nth-child(3) {
            width: 290px;
            text-align: left;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1621px) {
              width: 140px;
            }
            @media all and (max-width: 1439px) {
              width: 115px;
            }
          }
          &:nth-child(4) {
            width: 420px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            &[role="cell"] {
              color: #7e7e7e;
              font-weight: 500;
              font-size: 16px;
              @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
              }
            }
            @media all and (max-width: 1621px) {
              width: 300px;
            }
            @media all and (max-width: 1439px) {
              width: 215px;
            }
          }
          &:nth-child(5) {
            width: 180px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            @media all and (max-width: 1439px) {
              width: 120px;
            }
          }
        }
      }
    }
  }
  .engagement {
    border-radius: 12px;
    background-color: #1971eb;
    color: #ffffff;
    font-size: 16px;
    font-family: "robotomedium";
    font-weight: 500;
    padding: 0px 10px;
    display: inline;
    float: right;
    @media all and (max-width: 1439px) {
      font-size: 12px;
      padding: 2px 8px;
    }
  }
  .email-btn {
    color: #7e7e7e;
    padding-right: 15px;
    height: 38px;
    background-color: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    font-size: 16px;
    &:focus > span,
    &:active > span,
    > span {
      position: static;
      padding-left: 4px;
      margin-top: 8px;
      float: right;
    }
  }
`;
